import { useMemo } from 'react';
import useOrderFlow from '../order/useOrderFlow';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';

/**
 * The first value that holds something. The checkout keeps an untouched invoice field as an empty
 * string, and `??` treats that as a value: with no invoice address the street came out empty and
 * the billing line read as just the delivery house number ("6-9").
 */
function firstFilled<T extends string | null | undefined>(...values: T[]): T | undefined {
  return values.find((value) => typeof value === 'string' && value.trim() !== '');
}

export default function useBillingInfo() {
  const { company } = useSelector((store: StoreInterface) => store.initialData);
  const { profile } = useSelector((state: StoreInterface) => state.profile);
  const { data } = useOrderFlow();

  const billingFirstName = useMemo(
    () => firstFilled(data.invoice_first_name, data.customer_first_name, profile?.first_name),
    [data.invoice_first_name, data.customer_first_name, profile?.first_name],
  );

  const billingLastName = useMemo(
    () => firstFilled(data.invoice_last_name, data.customer_last_name, profile?.last_name),
    [data.invoice_last_name, data.customer_last_name, profile?.last_name],
  );

  const billingFullName = useMemo(() => {
    const value = `${billingFirstName ?? ''} ${billingLastName ?? ''}`.trim();

    return value ? value : null;
  }, [billingFirstName, billingLastName]);

  const billingEmail = useMemo(
    () => firstFilled(data.invoice_email, profile?.email, data.customer_email),
    [data.invoice_email, profile?.email, data.customer_email],
  );

  const billingPhoneNumber = useMemo(
    () => firstFilled(profile?.phone_number, data.customer_phone_number),
    [profile?.phone_number, data.customer_phone_number],
  );

  // "Hauptstraße 12" → street and number; no invoice or profile address falls back to the delivery one.
  const addressParts = useMemo(
    () =>
      (firstFilled(data.invoice_address, profile?.address) ?? '')
        .trim()
        .split(/\s+/)
        .filter((part) => part !== ''),
    [data.invoice_address, profile?.address],
  );

  const billingStreetName = useMemo(() => {
    if (addressParts.length > 1) {
      return addressParts.slice(0, -1).join(' ');
    }

    return addressParts[0] ?? firstFilled(data.delivery_street_name);
  }, [addressParts, data.delivery_street_name]);

  const billingStreetNumber = useMemo(
    () => (addressParts.length > 1 ? addressParts[addressParts.length - 1] : firstFilled(data.delivery_street_number)),
    [addressParts, data.delivery_street_number],
  );

  const billingAddress = useMemo(() => {
    const value = `${billingStreetName ?? ''} ${billingStreetNumber ?? ''}`.trim();

    return value ? value : null;
  }, [billingStreetName, billingStreetNumber]);

  const billingCity = useMemo(
    () => firstFilled(data.invoice_city, profile?.city, data.delivery_city),
    [data.invoice_city, profile?.city, data.delivery_city],
  );

  const billingState = useMemo(
    () => firstFilled(profile?.state, data.delivery_state, company?.state),
    [profile?.state, data.delivery_state, company?.state],
  );

  const billingCountry = useMemo(
    () => firstFilled(profile?.country, data.delivery_country, company?.country),
    [profile?.country, data.delivery_country, company?.country],
  );

  const billingZipCode = useMemo(
    () => firstFilled(data.invoice_zip_code, profile?.zip_code, data.delivery_zip_code),
    [data.invoice_zip_code, profile?.zip_code, data.delivery_zip_code],
  );

  // Memoised: these feed the Stripe Elements options, which re-apply on every new object.
  const stripeFormat = useMemo(
    () => ({
      name: billingFullName,
      email: billingEmail,
      address: {
        line1: billingAddress,
        city: billingCity,
        state: billingState,
        country: billingCountry,
        postal_code: billingZipCode,
      },
    }),
    [billingFullName, billingEmail, billingAddress, billingCity, billingState, billingCountry, billingZipCode],
  );

  const adyenAddressFormat = useMemo(
    () => ({
      street: billingStreetName,
      houseNumberOrName: billingStreetNumber,
      city: billingCity,
      stateOrProvince: billingState,
      country: billingCountry,
      postalCode: billingZipCode,
    }),
    [billingStreetName, billingStreetNumber, billingCity, billingState, billingCountry, billingZipCode],
  );

  return {
    billingFirstName,
    billingLastName,
    billingFullName,
    billingStreetName,
    billingStreetNumber,
    billingAddress,
    billingCity,
    billingState,
    billingCountry,
    billingZipCode,
    billingEmail,
    billingPhoneNumber,
    stripeFormat,
    adyenAddressFormat,
  };
}
